(() => {
  'use strict';

  const ELEMENT_NAME = 'look-card';

  if (customElements.get(ELEMENT_NAME)) return;

  class LookCard extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.swatches = [...this.querySelectorAll('[data-look-swatch]')];
      if (!this.swatches.length) return;

      this.controller = new AbortController();
      this.swatches.forEach((swatch) => {
        swatch.addEventListener('click', (event) => this.onSwatchClick(event, swatch), {
          signal: this.controller.signal,
        });
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
    }

    onSwatchClick(event, swatch) {
      // Modifier clicks keep the native "open variant in new tab" behaviour.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
      event.preventDefault();

      try {
        this.selectSwatch(swatch);
      } catch (error) {
        console.error(`<${ELEMENT_NAME}>: could not switch color, following link instead.`, error);
        window.location.href = swatch.href;
      }
    }

    selectSwatch(swatch) {
      const { color, price, compare, imageSrc, imageSrcset } = swatch.dataset;

      this.swatches.forEach((item) => {
        const active = item === swatch;
        item.classList.toggle('is-selected', active);
        if (active) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });

      this.querySelectorAll('[data-look-link]').forEach((link) => {
        link.href = swatch.href;
      });

      const colorLabel = this.querySelector('[data-look-color]');
      if (colorLabel && color) colorLabel.textContent = color;

      this.updateImage(imageSrc, imageSrcset);
      this.updatePrice(price, compare);
      this.syncQuickAdd(color);
    }

    updateImage(src, srcset) {
      if (!src) return;

      // With a card image override only the stacked-layout photo follows the color; the desktop image stays.
      const source = this.querySelector('[data-look-source]');
      if (source) {
        source.srcset = srcset || src;
        return;
      }

      const image = this.querySelector('.look-card__media img');
      if (!image) return;
      if (srcset) image.srcset = srcset;
      else image.removeAttribute('srcset');
      image.src = src;

      const backdrop = this.querySelector('[data-look-backdrop]');
      if (backdrop) backdrop.src = this.smallestSource(src, srcset);
    }

    // The blurred backdrop needs no detail, so it takes the first (smallest) srcset candidate.
    smallestSource(src, srcset) {
      const firstCandidate = srcset?.split(',')[0]?.trim().split(/\s+/)[0];
      return firstCandidate || src;
    }

    updatePrice(price, compare) {
      const priceEl = this.querySelector('[data-look-price]');
      if (!priceEl || !price) return;

      const nodes = [];
      const hidden = (text) => {
        const span = document.createElement('span');
        span.className = 'visually-hidden';
        span.textContent = text;
        return span;
      };
      const strong = document.createElement('strong');
      strong.textContent = price;

      if (compare) {
        const s = document.createElement('s');
        s.textContent = compare;
        nodes.push(hidden(priceEl.dataset.labelSale || ''), strong, hidden(priceEl.dataset.labelRegular || ''), s);
      } else {
        nodes.push(strong);
      }
      priceEl.replaceChildren(...nodes);
    }

    // Preselects the chosen color in the theme's <inline-quick-add> panel.
    syncQuickAdd(color) {
      const quickAdd = this.querySelector('inline-quick-add');
      const position = this.dataset.colorPosition;
      if (!quickAdd || !position || !color || !Array.isArray(quickAdd.selected)) return;

      const optionIndex = quickAdd.optionEls?.findIndex((el) => el.dataset.optionPosition === position) ?? -1;
      if (optionIndex < 0) return;

      quickAdd.selected[optionIndex] = color;
      quickAdd._renderSelection?.();
      quickAdd._updateAvailability?.();
      quickAdd._updateVariant?.();
    }
  }

  customElements.define(ELEMENT_NAME, LookCard);
})();
